import { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Slider from "@mui/material/Slider";
import Cropper from "react-easy-crop";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

interface ImageCropModalProps {
    open: boolean;
    imageSrc: string;
    onClose: () => void;
    onCropComplete: (croppedFile: File) => void;
    darkMode?: boolean;
    aspectRatio?: number; // Optional aspect ratio, default to 9/16
}

interface ImageCropModalState {
    crop: { x: number; y: number };
    zoom: number;
    croppedAreaPixels: any;
    darkMode: boolean;
}

class ImageCropModal extends Component<ImageCropModalProps, ImageCropModalState> {
    constructor(props: ImageCropModalProps) {
        super(props);
        this.state = {
            crop: { x: 0, y: 0 },
            zoom: 1,
            croppedAreaPixels: null,
            darkMode: props.darkMode ?? false,
        };
    }

    componentDidUpdate(prevProps: ImageCropModalProps) {
        if (this.props.darkMode !== prevProps.darkMode) {
            this.setState({ darkMode: this.props.darkMode ?? false });
        }
    }

    onCropChange = (crop: { x: number; y: number }) => {
        this.setState({ crop });
    };

    onZoomChange = (zoom: number) => {
        this.setState({ zoom });
    };

    onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
        this.setState({ croppedAreaPixels });
    };

    createCroppedImage = async () => {
        try {
            const { imageSrc } = this.props;
            const { croppedAreaPixels } = this.state;

            const image = await this.createImage(imageSrc);
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            if (!ctx) {
                throw new Error("Canvas context를 가져올 수 없습니다");
            }

            // 원본 이미지의 픽셀 정보를 사용하여 캔버스 크기 설정
            canvas.width = croppedAreaPixels.width;
            canvas.height = croppedAreaPixels.height;

            ctx.drawImage(
                image,
                croppedAreaPixels.x,
                croppedAreaPixels.y,
                croppedAreaPixels.width,
                croppedAreaPixels.height,
                0,
                0,
                croppedAreaPixels.width,
                croppedAreaPixels.height
            );

            return new Promise<File>((resolve, reject) => {
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error("이미지 변환에 실패했습니다"));
                            return;
                        }
                        const file = new File([blob], "cropped-image.jpg", {
                            type: "image/jpeg",
                            lastModified: Date.now(),
                        });
                        resolve(file);
                    },
                    "image/jpeg",
                    0.95
                );
            });
        } catch (error) {
            console.error("크롭 오류:", error);
            throw error;
        }
    };

    createImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.addEventListener("load", () => resolve(image));
            image.addEventListener("error", (error) => reject(error));
            image.src = url;
        });
    };

    handleSave = async () => {
        try {
            const croppedFile = await this.createCroppedImage();
            this.props.onCropComplete(croppedFile);
            this.props.onClose();
        } catch (error) {
            console.error("크롭 저장 오류:", error);
        }
    };

    getAspectRatioLabel = () => {
        const ratio = this.props.aspectRatio ?? 9 / 16;
        if (Math.abs(ratio - 9 / 16) < 0.01) return "9:16";
        if (Math.abs(ratio - 16 / 9) < 0.01) return "16:9";
        if (Math.abs(ratio - 4 / 3) < 0.01) return "4:3";
        if (Math.abs(ratio - 3 / 4) < 0.01) return "3:4";
        if (Math.abs(ratio - 1) < 0.01) return "1:1";
        return "Custom";
    };

    render() {
        const { open, onClose, aspectRatio = 9 / 16 } = this.props;
        const { crop, zoom, darkMode } = this.state;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#1a2035" : "#fff",
                        color: darkMode ? "#fff" : "#000",
                    },
                }}
            >
                <DialogTitle>이미지 크롭 ({this.getAspectRatioLabel()} 비율)</DialogTitle>
                <DialogContent>
                    <MDBox sx={{ position: "relative", width: "100%", height: 400, mb: 3 }}>
                        <Cropper
                            image={this.props.imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={aspectRatio}
                            onCropChange={this.onCropChange}
                            onZoomChange={this.onZoomChange}
                            onCropComplete={this.onCropComplete}
                        />
                    </MDBox>
                    <MDBox>
                        <MDTypography variant="caption" color="text">
                            확대/축소
                        </MDTypography>
                        <Slider
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            onChange={(e, value) => this.onZoomChange(value as number)}
                            sx={{
                                color: darkMode ? "#49a3f1" : "#1976d2",
                            }}
                        />
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={onClose} variant="outlined" color="secondary">
                        취소
                    </MDButton>
                    <MDButton onClick={this.handleSave} variant="gradient" color="info">
                        크롭 완료
                    </MDButton>
                </DialogActions>
            </Dialog>
        );
    }
}

export default ImageCropModal;
